import RegistryForHash from '../interfaces/Registry';
import {
  Contexts,
  DandelionService,
  Helpers,
  NamedBindings,
  UserService,
  getContainer,
} from '@dandelion/core';
import { ReadmissionService } from './ReadmissionService';
import { NamedServices } from '../enum/NamedServices';
import { AdmissionIdAndHash } from "../repository/ReadmissionChainForPatientKeyStorage";
import { inject } from "inversify";
import { ReadmissionStorageInterface } from "../interfaces/ReadmissionStorageInterface";
import { Crypt } from '../utils/Crypt';

export type ReadmissionHistory = { caseId: string, admissionDate: string, prevAdmission: ReadmissionHistory | undefined };

export class ReadmissionManagerRunner extends DandelionService {
  private cryptedHash: string = '';
  private admissionDate: string = '';
  private readmissionService = getContainer(Contexts.SHARED).get(NamedServices.ReadmissionService) as ReadmissionService;
  private userService = getContainer().get(NamedBindings.UserService) as UserService;

  constructor(
    @inject("ReadmissionStorage") private readmissionStorage: ReadmissionStorageInterface,
    private caseId: string,
    private model: any
  ) {
    super();
    this.readmissionService.registerRunner(this);
  }

  async checkReadmissionsHypothesis(admissionsWithPatientKey: Array<AdmissionIdAndHash> = []) {
    if (!this.model) {
      return false;
    }
    const variablesForHash: RegistryForHash = {
      name: this.model?.core?.personalData?.name,
      surname: this.model?.core?.personalData?.surname,
      birthDate: this.model?.core?.personalData?.birthDate,
      centreCode: this.userService.getCurrentUser()?.value?.centre || "*",
    };
    this.cryptedHash = await Crypt.generateHash(variablesForHash);
    this.admissionDate = Helpers.getObjByPath(ReadmissionService.VARIABLE_FOR_READMISSION_DATE, this.model);
    const readmissionHypothesis = await this.readmissionStorage.getPrevAdmissionsHypothesis(this.cryptedHash) ?? [];

    const filteredReadmissionHypothesis = readmissionHypothesis.filter((rH) =>
        !admissionsWithPatientKey.find((admWithPatientKey) => rH.caseId === admWithPatientKey.lastAdmissionIdFromChain)
    );

    if (filteredReadmissionHypothesis && filteredReadmissionHypothesis.length > 0) {
      this.readmissionService.patientHistory.value = filteredReadmissionHypothesis;
      this.readmissionService.firstCase.value = this.admissionDate;
      this.readmissionService.showUserDialog.value = true;
      return true;
    } else {
      await this.saveHashForNotReadmission();
      return false;
    }
  }

  async waitForDialogToClose(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const checkDialogStatus = () => {
        if (!this.readmissionService.showUserDialog.value) {
          resolve(this.readmissionService.dialogResult.value);
          this.unregisterRunner();
        } else {
          setTimeout(checkDialogStatus, 100);
        }
      };
      checkDialogStatus();
    });
  }

  unregisterRunner() {
    this.readmissionService.unregisterRunner(this);
  }

  async updateAdmissionsChain(selectedHistory: ReadmissionHistory) {
    this.model.previousAdmissionId = selectedHistory.caseId;
    this.model.hashForReadmission = this.cryptedHash;
    const currentAdmissionsChains = await this.readmissionStorage.getPrevAdmissionsHypothesis(this.cryptedHash);

    if (!currentAdmissionsChains) {
      throw new Error(`no Readmission chain found for hash, ${this.cryptedHash}, even if a readmission was indicated `);
    }
    const indexOfSelectedChain = currentAdmissionsChains.findIndex((admission) => admission.caseId === selectedHistory.caseId );
    if (indexOfSelectedChain !== undefined) {
      // We append the new readmission to the top of the chain
      currentAdmissionsChains[indexOfSelectedChain] = {
        caseId: this.caseId,
        admissionDate: this.admissionDate,
        prevAdmission: selectedHistory
      };
    }
    await this.readmissionStorage.save(this.cryptedHash, currentAdmissionsChains);
    this.readmissionService.showUserDialog.value = false;
  }

  async saveHashForNotReadmission() {
    const currentAdmissionsChains = await this.readmissionStorage.getPrevAdmissionsHypothesis(this.cryptedHash) ?? [];
    currentAdmissionsChains.push({ caseId: this.caseId, admissionDate: this.admissionDate, prevAdmission: undefined })
    await this.readmissionStorage.save(this.cryptedHash, currentAdmissionsChains);
    this.readmissionService.showUserDialog.value = false;
  }

  async getReadmissionHypothesis(hash: string){
    return (await this.readmissionStorage.getPrevAdmissionsHypothesis(hash));
  }
}
